import { useState } from 'react';
import { PageHeader, SectionLabel, Card, Stat, SampleBanner, StatusPill, Row } from '../components/ui.js';

interface AlertLog {
  id: string;
  ts: string;
  account: string | null;
  severity: 'critical' | 'warn' | 'info';
  title: string;
  detail: string;
  channel: ('email' | 'telegram' | 'sms')[];
  acked_at: string | null;
  resolved_at: string | null;
}
interface AlertRule {
  id: string;
  name: string;
  description: string;
  trigger: string;
  severity: 'critical' | 'warn' | 'info';
  channels: ('email' | 'telegram' | 'sms')[];
  enabled: boolean;
}

const ALERT_LOG: AlertLog[] = [
  { id: 'al-012', ts: '2026-05-04T13:14:00Z', account: 'Glisten Glendale', severity: 'warn', title: 'Rank drop on "cosmetic dentist glendale az"', detail: 'Position dropped 12 → 14 in the last 24h. Recovery content drafted, awaiting approval.', channel: ['email', 'telegram'], acked_at: '2026-05-04T13:21:00Z', resolved_at: null },
  { id: 'al-011', ts: '2026-05-02T05:14:00Z', account: 'Glisten Mesa', severity: 'warn', title: 'Lighthouse perf regressed on homepage', detail: '92 → 78 (-14). Likely cause: 1 third-party script added. Auto-investigation queued.', channel: ['email', 'telegram'], acked_at: '2026-05-02T07:08:00Z', resolved_at: '2026-05-03T05:14:00Z' },
  { id: 'al-010', ts: '2026-04-29T05:14:00Z', account: 'Glisten Gilbert', severity: 'warn', title: 'Audit found broken image on /services/implants', detail: 'src=/wp-content/uploads/implants-hero-2024.jpg returns 404. Auto-fix queued.', channel: ['email'], acked_at: '2026-04-29T08:00:00Z', resolved_at: '2026-04-29T11:42:00Z' },
  { id: 'al-009', ts: '2026-04-22T03:00:00Z', account: null, severity: 'critical', title: 'GBP API timeout (all customers affected)', detail: 'Google Business Profile API returned 503 for 14 minutes during scheduled sweep. Retried successfully on 3rd attempt.', channel: ['email', 'telegram', 'sms'], acked_at: '2026-04-22T03:04:00Z', resolved_at: '2026-04-22T03:18:00Z' },
  { id: 'al-008', ts: '2026-04-19T05:14:00Z', account: 'Glisten Gilbert', severity: 'warn', title: 'Cost cap warning: $32 spent in 24h', detail: 'Daily cap is $40 with alert at $30. One unusually long content run hit 6 iterations.', channel: ['email'], acked_at: '2026-04-19T07:00:00Z', resolved_at: '2026-04-19T07:00:00Z' },
  { id: 'al-007', ts: '2026-04-15T02:14:00Z', account: null, severity: 'info', title: 'Anthropic credit threshold reached', detail: 'Auto-reload triggered on Visa ending 8655. New balance: $200.', channel: ['email'], acked_at: '2026-04-15T08:00:00Z', resolved_at: '2026-04-15T08:00:00Z' },
];

const ALERT_RULES: AlertRule[] = [
  { id: 'r-1', name: 'Backup failure', description: 'Any nightly backup that does not complete or hash-verify.', trigger: 'backup.status = failed OR backup not run within 25h', severity: 'critical', channels: ['email', 'telegram', 'sms'], enabled: true },
  { id: 'r-2', name: 'Rank drop ≥ 5', description: 'Tracked keyword falls 5+ positions in 24h.', trigger: 'rank.delta_24h <= -5', severity: 'warn', channels: ['email', 'telegram'], enabled: true },
  { id: 'r-3', name: 'Lighthouse regression', description: 'Performance score drops 10+ points day-over-day.', trigger: 'lighthouse.perf.delta_24h <= -10', severity: 'warn', channels: ['email', 'telegram'], enabled: true },
  { id: 'r-4', name: 'GBP API outage', description: 'Three consecutive GBP requests fail.', trigger: 'gbp.consecutive_failures >= 3', severity: 'critical', channels: ['email', 'telegram', 'sms'], enabled: true },
  { id: 'r-5', name: 'Daily cost cap warning', description: 'Cost in 24h reaches alert threshold ($30).', trigger: 'cost.last_24h >= 30', severity: 'warn', channels: ['email'], enabled: true },
  { id: 'r-6', name: 'Daily cost cap exceeded', description: 'Cost in 24h exceeds hard cap ($40). Engine pauses.', trigger: 'cost.last_24h >= 40', severity: 'critical', channels: ['email', 'telegram', 'sms'], enabled: true },
  { id: 'r-7', name: 'Audit finding new fail', description: 'Weekly audit produces a new fail (not just fix).', trigger: 'audit.new_fails > 0', severity: 'warn', channels: ['email', 'telegram'], enabled: true },
  { id: 'r-8', name: 'Anthropic credit refill', description: 'Auto-reload triggered on Anthropic API credits.', trigger: 'anthropic.balance < threshold', severity: 'info', channels: ['email'], enabled: true },
  { id: 'r-9', name: 'Customer plan change', description: 'Customer upgrades or downgrades plan via Stripe.', trigger: 'stripe.subscription.updated', severity: 'info', channels: ['email', 'telegram'], enabled: true },
  { id: 'r-10', name: 'New customer onboarded', description: 'Stripe checkout completed + Bloom account provisioned.', trigger: 'onboarding.completed', severity: 'info', channels: ['email', 'telegram'], enabled: true },
];

export function OperatorAlertsPage() {
  const [tab, setTab] = useState<'log' | 'rules'>('log');
  const open = ALERT_LOG.filter((a) => !a.resolved_at);
  const acked = ALERT_LOG.filter((a) => a.acked_at && !a.resolved_at);
  const critical = ALERT_LOG.filter((a) => a.severity === 'critical');

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Operator · Alerts"
        title={<>What needs your <em className="text-cream/80">attention.</em></>}
        subtitle="Past alerts log + rules config. Routes through email, Telegram, and SMS depending on severity."
      />
      <SampleBanner />

      <section>
        <SectionLabel>Status</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Open" value={open.length} tone={open.length > 0 ? 'warn' : 'ok'} />
          <Stat label="Acked, unresolved" value={acked.length} tone={acked.length > 0 ? 'warn' : 'ok'} />
          <Stat label="Critical (90d)" value={critical.length} tone={critical.length > 0 ? 'bad' : 'ok'} />
          <Stat label="Active rules" value={ALERT_RULES.filter((r) => r.enabled).length} hint={`of ${ALERT_RULES.length}`} />
        </div>
      </section>

      <div className="flex items-center gap-1 border-b border-cream/[0.08] -mb-6">
        {(['log', 'rules'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors relative ${
              tab === t ? 'text-cream' : 'text-cream/40 hover:text-cream/70'
            }`}
          >
            {t === 'log' ? 'Past alerts' : 'Rules'}
            {tab === t && <span className="absolute left-2 right-2 bottom-0 h-px bg-cream/85" />}
          </button>
        ))}
      </div>

      {tab === 'log' && (
        <section>
          <div className="space-y-3">
            {ALERT_LOG.map((a) => (
              <Card key={a.id}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <StatusPill tone={a.severity === 'critical' ? 'bad' : a.severity === 'warn' ? 'warn' : 'info'}>{a.severity}</StatusPill>
                      <span className="text-[11px] text-cream/45">{a.account ?? 'platform-wide'}</span>
                      <span className="text-cream/30 text-[11px]">·</span>
                      <span className="text-[11px] text-cream/45 tabular-nums">{new Date(a.ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' })}</span>
                    </div>
                    <div className="text-cream/90">{a.title}</div>
                    <div className="text-[11px] text-cream/55 mt-2">{a.detail}</div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {a.channel.map((c) => (
                        <span key={c} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-cream/[0.06] text-cream/60 border border-cream/[0.12]">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div className="shrink-0 text-right space-y-1">
                    {a.resolved_at ? (
                      <StatusPill tone="ok">resolved</StatusPill>
                    ) : a.acked_at ? (
                      <StatusPill tone="warn">acked</StatusPill>
                    ) : (
                      <StatusPill tone="bad">open</StatusPill>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {tab === 'rules' && (
        <section>
          <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-cream/40 border-b border-cream/[0.06]">
                  <th className="px-5 py-3 font-medium">Rule</th>
                  <th className="px-3 py-3 font-medium">Severity</th>
                  <th className="px-3 py-3 font-medium">Channels</th>
                  <th className="px-5 py-3 font-medium text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {ALERT_RULES.map((r) => (
                  <tr key={r.id} className="border-b border-cream/[0.04] last:border-0 hover:bg-cream/[0.02] transition-colors">
                    <td className="px-5 py-3">
                      <div className="text-cream/90">{r.name}</div>
                      <div className="text-[11px] text-cream/45 mt-0.5">{r.description}</div>
                      <div className="text-[10px] text-cream/35 mt-1 font-mono">{r.trigger}</div>
                    </td>
                    <td className="px-3 py-3"><StatusPill tone={r.severity === 'critical' ? 'bad' : r.severity === 'warn' ? 'warn' : 'info'}>{r.severity}</StatusPill></td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.channels.map((c) => (
                          <span key={c} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-cream/[0.06] text-cream/60 border border-cream/[0.12]">{c}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <StatusPill tone={r.enabled ? 'ok' : 'neutral'}>{r.enabled ? 'enabled' : 'off'}</StatusPill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <Card>
              <div className="text-[10px] uppercase tracking-[0.22em] text-cream/40 mb-3">Channel routing</div>
              <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3">
                <Row label="Email">team@holocronhq.com</Row>
                <Row label="Telegram">@BloomOpsBot · 8762910164</Row>
                <Row label="SMS">+1 480-843-7996 (critical only)</Row>
              </dl>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
}
